import { useEffect, ReactNode } from 'react';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { toast } from '@/stores/useToastStore';
import { NetworkStatusContext } from './networkStatusContextStore';

interface NetworkStatusProviderProps {
    children: ReactNode;
}

/**
 * Network Status Provider
 *
 * Provides network status to the entire application and shows
 * toast notifications when connectivity changes.
 *
 * @example
 * // In App.tsx
 * <NetworkStatusProvider>
 *   <App />
 * </NetworkStatusProvider>
 */
export function NetworkStatusProvider({ children }: NetworkStatusProviderProps) {
    const { isOnline, wasOffline, lastOfflineAt, lastOnlineAt } = useNetworkStatus();

    // Show toast when going offline
    useEffect(() => {
        if (!isOnline && lastOfflineAt) {
            toast.warning('You are offline. Some features may not be available.');
        }
    }, [isOnline, lastOfflineAt]);

    // Show toast when coming back online
    useEffect(() => {
        if (isOnline && wasOffline && lastOnlineAt) {
            toast.success('You\'re back online!');
        }
    }, [isOnline, wasOffline, lastOnlineAt]);

    return (
        <NetworkStatusContext.Provider value={{ isOnline, wasOffline }}>
            {children}
        </NetworkStatusContext.Provider>
    );
}
